
























import Debug from 'debug'
import { Vue, Component, Prop, Emit, Watch } from 'vue-property-decorator';
import { resolve } from '@/app/container';
import { EventStream, EventStreamSymbol } from '@/app/event-stream/event-stream';
import {
  SectionInteractionEvent,
  SectionInteractedEventName
} from '@/app/statistics/events/section-interaction-events';
import { FLIPCARD_TYPE } from '@/app/statistics/handlers/flipcard-items-completion-handler';
import WrapperCommonComponent from '@/app/presenter/components/wrapper-common-component.vue';
import { FlipcardState, FlipcardItem, FlipcardContent } from './flipcard-interfaces';
import * as Api from "@/app/interaction/api";
import { TranslateResult } from 'vue-i18n';
import i18n from '@/plugins/i18n';
import { restrictedThemes } from '@/app/shared/utils'

const debug = Debug('flipcard-component')

interface ReferenceMap {
  [key: number]: number;
}

interface Item {
  label: string;
  content: string;
  touched: boolean;
}

@Component({
  components: {
    WrapperCommonComponent
  }
})
export default class FlipcardComponent extends Vue
  implements Api.InteractiveComponent<FlipcardState, FlipcardContent> {

  @Prop({
    required: true
  })
  public meta: Api.InteractionComponentMeta;

  @Prop({
    required: true
  })
  public content: FlipcardContent;

  @Prop({
    required: true
  })
  public state: FlipcardState | null;

  private internalItems: Item[] = [];

  private activeIndex: number = 0;

  private isMobile: boolean;

  private hasActive(): boolean {
    return this.activeIndex !== 0;
  }

  public get colorBackground(): boolean {
    return restrictedThemes(['eucom'])
  }

  private mounted() {
    this.activeIndex = 0;

    this.internalItems = this.content.items.map((item, index) => ({
      ...item,
      touched: this.state?.touched[index] || false,
    }));

    if (this.state) {
      this.activeIndex = this.state.activeIndex;
    }

    this.$nextTick(() => this.handleElements());
  }

  private created() {
    this.initEventHandling();
  }

  private destroyed() {
    this.destroyEventHandling();
  }

  private toggleCard(index: number): void {
    this.activeIndex = this.activeIndex === index ? 0 : index;

    let progressChanged = false;
    if (!this.internalItems[index - 1].touched) {
      this.internalItems[index - 1].touched = true;
      progressChanged = true;
    }

    this.emitInteractionStateChanged();
    if (progressChanged) {
      this.emitInteractionResultChanged();
    }

    this.handleElements();
  }

  private initEventHandling() {
    window.addEventListener('resize', this.setScreenSize);
    this.setScreenSize();
  }

  private destroyEventHandling() {
    window.removeEventListener('resize', this.setScreenSize);
  }

  private setScreenSize() {
    const width = window.innerWidth;
    const laptopMobileBreakPoint = 640;

    this.isMobile = width < laptopMobileBreakPoint;

    this.handleElements();
    this.$forceUpdate();
  }

  private getLeftP(count: number) {
    const referenceMap = {
      3: 0,
      4: 50,
      5: 70
    } as ReferenceMap;

    for (let i = 6; i <= count; i++) {
      const val =
        1.4 * (referenceMap[i - 1] - referenceMap[i - 2]) + referenceMap[i - 2];
      referenceMap[i] = val;
    }

    return referenceMap[count];
  }

  private handleElements() {
    if (this.isMobile) {
      this.handleElementsOnMobile();
    } else {
      this.handleElementsOnDesktop();
    }
  }

  private handleElementsOnMobile() {
    if (!this.$el) {
      return;
    }

    const cws = this.$el.querySelectorAll<HTMLElement>('.card-wrapper');
    cws.forEach((cw: HTMLElement) => {
      cw.style.width = `60%`;
      cw.style.zIndex = 'auto';
      cw.style.transform = `none`;
    });
  }

  private handleElementsOnDesktop() {
    if (!this.$el) {
      return;
    }

    const defaultSpacing = 10;
    const cws = this.$el.querySelectorAll<HTMLElement>('.card-wrapper');
    const cwCount = cws.length;
    const cwWidthPercentage = 100 / cwCount;
    const cwMaxLeft = cwCount * defaultSpacing;
    const cwDiffs = [0];
    const cwActiveDiffs = [0];

    let cwDiffStep = 0;
    let cwActiveDiffStep = 0;

    if (cwCount > 1) {
      cwDiffStep = cwMaxLeft / (cwCount - 1);
      cwActiveDiffStep = cwMaxLeft / (cwCount - 2);
    }

    for (let i = 1; i < cwCount; i++) {
      cwDiffs.push(i * cwDiffStep);
      cwActiveDiffs.push(i * cwActiveDiffStep);
    }

    let j = 0;
    let reverseJ = cwCount - 1;

    cws.forEach((cw: HTMLElement) => {
      cw.style.width = `calc(${cwWidthPercentage}% - ${defaultSpacing}px)`;
      //// cw.style.width = `calc(33.33% - 20px)`;
      const leftPercentage = j * 100;
      //// const leftPercentage = i === 0 ? 0 : (100 / (cwCount-1) * 2 * i);
      const leftDiff = cwDiffs[j];
      //// const leftDiff = i === 0 ? 0 : (cwDiffStep*(i-1));
      let zIndex: number;

      cwCount === 2 && this.activeIndex !== 1 ? zIndex = j : zIndex = reverseJ--;
      cwCount === 2 && !this.activeIndex
        ? setTimeout(() => cw.style.zIndex = zIndex.toString(), 1000)
        : cw.style.zIndex = zIndex.toString();

      cw.style.transform = `translateX(calc(${leftPercentage}% + ${leftDiff}px))`;

      j++;
    });

    if (this.hasActive()) {
      const ai = this.activeIndex || 1;
      const leftP = this.getLeftP(cwCount);
      if (ai === 1) {
        let diffIndex = 0;
        for (let i = ai; i < cwCount - 1; i++) {
          const cw = cws.item(i) as HTMLElement;
          const lp = 200 + leftP * (i - 1);
          const ld = cwActiveDiffs[diffIndex++];
          cw.style.transform = `translateX(calc(${lp}% + ${ld}px))`;
        }
      } else if (ai === cwCount) {
        let diffIndex = 1;
        for (let i = 1; i < cwCount - 1; i++) {
          const cw = cws.item(i) as HTMLElement;
          const lp = leftP * i;
          const ld =
            cwActiveDiffs[diffIndex++] - (cwCount > 4 ? cwDiffStep : 0);
          cw.style.transform = `translateX(calc(${lp}% + ${ld}px))`;
        }
      } else {
        let diffIndex = ai - 1;
        let di = 0;
        for (let i = ai; i < cwCount - 1; i++) {
          const cw = cws.item(i) as HTMLElement;
          const lp = (ai + 1) * 100 + leftP * di++;
          const ld = cwDiffs[diffIndex++];
          cw.style.transform = `translateX(calc(${lp}% + ${ld}px))`;
        }
      }
    }
  }

  @Emit('interaction-result-changed')
  public emitInteractionResultChanged() {
    let touchedItems = 0;

    for (let i in this.internalItems) {
      if (this.internalItems[i].touched) {
        touchedItems ++;
      }
    }

    const progress = parseFloat((touchedItems / this.internalItems.length).toFixed(2));

    const result: Api.Result = {
      progress,
      status: progress < 1 ? 'incomplete' : 'completed'
    }

    debug('emitInteractionResultChanged', result);

    return result
  }

  @Emit('interaction-state-changed')
  public emitInteractionStateChanged() {
    const state = {
      activeIndex: this.activeIndex,
      touched: this.internalItems.map(item => item.touched || false)
    };

    debug('emitInteractionStateChanged', state);

    return state;
  }

  private t(key: string, params: any = {}): TranslateResult {
    return i18n.t(key, params);
  }
}
